import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cacheStatistics, clearCachedReads, type CacheStatistics } from './cache';
import { deviceHasNetwork, isOnline, startConnectivityWatch, subscribeToConnectivity } from './connectivity';
import { offlineCryptoAvailable } from './crypto';
import { emitOffline, lastOfflineCopyAt, subscribeToOffline } from './events';
import { createOfflineInterceptor, setOfflineIdentity } from './interceptor';
import {
  countOutbox,
  listOutbox,
  openOutboxDetail,
  type OutboxCounts,
  type OutboxRecord,
  type QueuedDetail,
} from './outbox';
import { setOfflineRequestRuntime } from './runtime';
import { discardQueuedWrite, flushOutbox, readSyncState, retryQueuedWrite, type SyncResult, type SyncState } from './sync';
import { offlineStoreAvailable } from './store';
import { readVaultSummary, type VaultSummary, vaultUnlocked, wipeVault } from './vault';

/**
 * Installed at import time rather than in an effect.
 *
 * Effects run child-first, so the session probe inside `AuthProvider` would otherwise fire before
 * its parent had installed anything, and the very first request of the session — the one that
 * decides whether the workspace opens offline — would bypass the offline layer.
 *
 * It is installed unconditionally. On a device that cannot hold an encrypted copy it stores
 * nothing and caches nothing, but it still turns a bare `fetch` failure into "this needs a
 * connection", which is a better answer for everyone.
 */
setOfflineRequestRuntime(createOfflineInterceptor());

// Seeds the identity from the enrolled vault so a cold, offline start already knows whose copy
// this is. `AuthProvider` replaces it with the live session as soon as there is one.
void readVaultSummary().then((summary) => {
  if (summary !== null) setOfflineIdentity({ userId: summary.userId, tenantId: null });
});

/**
 * Whether this browser can protect an offline copy at all: Web Crypto needs a secure origin, and
 * IndexedDB is the only store durable enough to hold a queue of unsent writes.
 */
function offlineSupported(): boolean {
  return offlineCryptoAvailable() && offlineStoreAvailable();
}

export interface QueuedWrite {
  readonly record: OutboxRecord;
  readonly detail: QueuedDetail | null;
}

interface OfflineContextValue {
  /** False where the offline copy cannot be protected: no Web Crypto, or no IndexedDB. */
  readonly supported: boolean;
  readonly online: boolean;
  /** True once this device holds an offline copy. */
  readonly enabled: boolean;
  /** True while the data key is in memory, so reads are copied and writes can be queued. */
  readonly unlocked: boolean;
  readonly vault: VaultSummary | null;
  readonly queue: readonly QueuedWrite[];
  readonly counts: OutboxCounts;
  readonly syncing: boolean;
  readonly syncState: SyncState;
  /** When the most recent screen was drawn from the device copy. */
  readonly servedFrom: string | null;
  readonly cache: CacheStatistics | null;
  /** Whether the browser has promised not to evict the copy under storage pressure. */
  readonly persisted: boolean | null;
  sync(): Promise<SyncResult>;
  retry(id: string): Promise<void>;
  discard(id: string): Promise<void>;
  clearCache(): Promise<void>;
  forgetDevice(): Promise<void>;
  requestPersistence(): Promise<boolean>;
  refresh(): Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

const noQueue: readonly QueuedWrite[] = [];
const noCounts = countOutbox([]);

/** How often an open window retries while it is still holding unsent writes. */
const RETRY_INTERVAL_MS = 60_000;

export function OfflineProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [vault, setVault] = useState<VaultSummary | null>(null);
  const [queue, setQueue] = useState<readonly QueuedWrite[]>(noQueue);
  const [syncState, setSyncState] = useState<SyncState>({ lastRunAt: null, lastSuccessAt: null, lastResult: null });
  const [syncing, setSyncing] = useState(false);
  const [cache, setCache] = useState<CacheStatistics | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [unlocked, setUnlocked] = useState(false);

  const online = useSyncExternalStore(subscribeToConnectivity, isOnline, () => true);
  const servedFrom = useSyncExternalStore(
    (listener) => subscribeToOffline('served-offline', listener),
    lastOfflineCopyAt,
    () => null,
  );

  const refreshQueue = useCallback(async () => {
    if (!offlineSupported()) return;
    try {
      const records = await listOutbox();
      // The details are sealed, so an item can only be described while the vault is open. A sealed
      // queue still reports how many writes it is holding, which is the part that matters most.
      const described = await Promise.all(records.map(async (record) => ({
        record,
        detail: await openOutboxDetail(record),
      })));
      setQueue(described);
    } catch {
      setQueue(noQueue);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!offlineSupported()) return;
    setUnlocked(vaultUnlocked());
    const [summary, state, statistics] = await Promise.all([
      readVaultSummary(),
      readSyncState(),
      cacheStatistics().catch(() => null),
    ]);
    setVault(summary);
    setSyncState(state);
    setCache(statistics);
    await refreshQueue();
  }, [refreshQueue]);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await flushOutbox();
      // Anything the server has now accepted makes every cached page a page behind, so the
      // workspace refetches rather than showing figures that predate its own writes.
      if (result.sent > 0) await queryClient.invalidateQueries();
      return result;
    } finally {
      setSyncing(false);
      await refresh();
    }
  }, [queryClient, refresh]);

  useEffect(() => {
    if (!offlineSupported()) return undefined;
    startConnectivityWatch();
    void refresh();

    const unsubscribe = [
      subscribeToOffline('queue-changed', () => void refreshQueue()),
      subscribeToOffline('vault-changed', () => void refresh()),
      subscribeToOffline('sync-changed', () => void readSyncState().then(setSyncState)),
    ];

    if (typeof navigator !== 'undefined' && navigator.storage?.persisted) {
      void navigator.storage.persisted().then(setPersisted).catch(() => setPersisted(null));
    }

    return () => {
      for (const stop of unsubscribe) stop();
    };
  }, [refresh, refreshQueue]);

  const counts = useMemo(() => countOutbox(queue.map((item) => item.record)), [queue]);
  const hasUnsent = counts.unsent > 0;

  // Everything that can plausibly mean "the connection is back": the browser saying so, the
  // window being looked at again, the service worker being woken by Background Sync, and a slow
  // timer for the case where none of those fire.
  useEffect(() => {
    if (!offlineSupported() || !hasUnsent) return undefined;

    // Gated on the device's network rather than on the workspace's belief about the server: the
    // send itself is what corrects that belief, so a stricter gate could never recover from a
    // spurious failure.
    const attempt = () => {
      if (deviceHasNetwork() && vaultUnlocked()) void sync();
    };

    attempt();
    const stopConnectivity = subscribeToConnectivity(attempt);
    const onVisible = () => {
      if (document.visibilityState === 'visible') attempt();
    };
    const onWorkerMessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === 'varza:sync-requested') attempt();
    };

    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onWorkerMessage);
    const timer = window.setInterval(attempt, RETRY_INTERVAL_MS);

    return () => {
      stopConnectivity();
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onWorkerMessage);
      window.clearInterval(timer);
    };
  }, [hasUnsent, sync]);

  const value = useMemo<OfflineContextValue>(() => ({
    supported: offlineSupported(),
    online,
    enabled: vault !== null,
    unlocked,
    vault,
    queue,
    counts: offlineSupported() ? counts : noCounts,
    syncing,
    syncState,
    servedFrom,
    cache,
    persisted,
    sync,
    retry: async (id: string) => {
      await retryQueuedWrite(id);
      await sync();
    },
    discard: async (id: string) => {
      await discardQueuedWrite(id);
      await refreshQueue();
    },
    clearCache: async () => {
      await clearCachedReads();
      await refresh();
    },
    forgetDevice: async () => {
      await wipeVault();
      emitOffline('vault-changed');
      await refresh();
    },
    requestPersistence: async () => {
      const granted = await navigator.storage?.persist?.() ?? false;
      setPersisted(granted);
      return granted;
    },
    refresh,
  }), [cache, counts, online, persisted, queue, refresh, refreshQueue, servedFrom, sync, syncState, syncing, unlocked, vault]);

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): OfflineContextValue {
  const context = useContext(OfflineContext);
  if (context === null) throw new Error('useOffline must be used within OfflineProvider.');
  return context;
}
